import React, { useState } from 'react';
import {
  Shield,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Info,
  Crown,
  Briefcase,
  BarChart3,
  Eye,
  Lock,
} from 'lucide-react';
import {
  useAuth,
  ROLES,
  ROLE_DESCRIPTIONS,
  PERMISSION_CATEGORIES,
  PERMISSIONS,
} from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';

const RolesPermissions = () => {
  const { user, hasRole, isLoading } = useAuth();
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState(['dashboard', 'loans']);
  const [selectedRole, setSelectedRole] = useState(null);


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const roleIcons = {
    [ROLES.AGENT]: Crown,
    [ROLES.LENDER]: Briefcase,
    [ROLES.BORROWER]: BarChart3,
  };

  const roleColors = {
    [ROLES.AGENT]: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      icon: 'text-purple-600',
      badge: 'bg-purple-100 text-purple-700',
    },
    [ROLES.LENDER]: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-700',
    },
    [ROLES.BORROWER]: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: 'text-green-600',
      badge: 'bg-green-100 text-green-700',
    },
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((c) => c !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const countPermissions = (role) => {
    return PERMISSIONS[role]?.length || 0;
  };

  const countCategoryPermissions = (role, categoryKey) => {
    const rolePerms = PERMISSIONS[role] || [];
    const categoryPerms = Object.keys(PERMISSION_CATEGORIES[categoryKey]?.permissions || {});
    return categoryPerms.filter(p => rolePerms.includes(p)).length;
  };


  if (!hasRole(ROLES.AGENT)) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Lock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('rolesPermissions.restrictedAccess')}</h2>
          <p className="text-slate-500">
            {t('rolesPermissions.noAccessMessage')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Shield className="w-7 h-7 text-blue-600" />
            {t('rolesPermissions.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('rolesPermissions.subtitle')}
          </p>
        </div>

        {}
        <div className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">
                {t('rolesPermissions.yourCurrentRole')}: <span className="font-bold">{ROLE_DESCRIPTIONS[user?.role]?.label}</span>
              </p>
              <p className="text-sm text-blue-600">
                {t('rolesPermissions.youHave')} {countPermissions(user?.role)} {t('rolesPermissions.permissionsOf')} {Object.values(PERMISSION_CATEGORIES).reduce((acc, cat) => acc + Object.keys(cat.permissions).length, 0)} {t('rolesPermissions.available')}
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries(ROLE_DESCRIPTIONS).map(([roleKey, roleInfo]) => {
            const RoleIcon = roleIcons[roleKey];
            const colors = roleColors[roleKey];
            const isCurrentRole = user?.role === roleKey;

            return (
              <button
                key={roleKey}
                onClick={() => setSelectedRole(selectedRole === roleKey ? null : roleKey)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedRole === roleKey
                    ? `${colors.bg} ${colors.border} ring-2 ring-offset-2 ring-${colors.text.replace('text-', '')}`
                    : `bg-white border-slate-200 hover:${colors.bg} hover:${colors.border}`
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <RoleIcon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  {isCurrentRole && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.badge}`}>
                      {t('rolesPermissions.you')}
                    </span>
                  )}
                </div>
                <h3 className={`font-semibold ${colors.text}`}>{roleInfo.label}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{roleInfo.description}</p>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-sm text-slate-600">
                    <span className="font-bold">{countPermissions(roleKey)}</span> permissions
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-semibold text-slate-800">{t('rolesPermissions.permissionMatrix')}</h2>
            <p className="text-sm text-slate-500">{t('rolesPermissions.permissionMatrixDesc')}</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 w-1/3">
                    {t('rolesPermissions.permission')}
                  </th>
                  {Object.entries(ROLE_DESCRIPTIONS).map(([roleKey, roleInfo]) => {
                    const colors = roleColors[roleKey];
                    return (
                      <th
                        key={roleKey}
                        className={`text-center px-4 py-3 text-sm font-semibold ${
                          selectedRole === roleKey ? colors.bg : ''
                        }`}
                      >
                        <span className={colors.text}>{roleInfo.label}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => {
                  const isExpanded = expandedCategories.includes(categoryKey);

                  return (
                    <React.Fragment key={categoryKey}>
                      {}
                      <tr
                        className="bg-slate-100 cursor-pointer hover:bg-slate-150"
                        onClick={() => toggleCategory(categoryKey)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                            <span className="font-semibold text-slate-700">{category.label}</span>
                          </div>
                        </td>
                        {Object.keys(ROLE_DESCRIPTIONS).map((roleKey) => {
                          const total = Object.keys(category.permissions).length;
                          const granted = countCategoryPermissions(roleKey, categoryKey);
                          const colors = roleColors[roleKey];

                          return (
                            <td
                              key={roleKey}
                              className={`text-center px-4 py-3 ${
                                selectedRole === roleKey ? colors.bg : ''
                              }`}
                            >
                              <span className={`text-sm font-medium ${
                                granted === total
                                  ? 'text-green-600'
                                  : granted > 0
                                  ? 'text-amber-600'
                                  : 'text-slate-400'
                              }`}>
                                {granted}/{total}
                              </span>
                            </td>
                          );
                        })}
                      </tr>

                      {}
                      {isExpanded &&
                        Object.entries(category.permissions).map(([permKey, permLabel]) => (
                          <tr key={permKey} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-2 pl-10">
                              <span className="text-sm text-slate-600">{permLabel}</span>
                            </td>
                            {Object.keys(ROLE_DESCRIPTIONS).map((roleKey) => {
                              const hasPermission = PERMISSIONS[roleKey]?.includes(permKey);
                              const colors = roleColors[roleKey];

                              return (
                                <td
                                  key={roleKey}
                                  className={`text-center px-4 py-2 ${
                                    selectedRole === roleKey ? colors.bg : ''
                                  }`}
                                >
                                  {hasPermission ? (
                                    <Check className="w-5 h-5 text-green-600 mx-auto" />
                                  ) : (
                                    <X className="w-5 h-5 text-slate-300 mx-auto" />
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {}
        {selectedRole && (
          <div className={`mt-8 p-6 rounded-xl border-2 ${roleColors[selectedRole].bg} ${roleColors[selectedRole].border}`}>
            <div className="flex items-start gap-4">
              {(() => {
                const RoleIcon = roleIcons[selectedRole];
                const colors = roleColors[selectedRole];
                return (
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                    <RoleIcon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                );
              })()}
              <div className="flex-1">
                <h3 className={`text-xl font-bold ${roleColors[selectedRole].text}`}>
                  {ROLE_DESCRIPTIONS[selectedRole].label}
                </h3>
                <p className="text-slate-600 mt-1">
                  {ROLE_DESCRIPTIONS[selectedRole].description}
                </p>

                <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => {
                    const rolePerms = PERMISSIONS[selectedRole] || [];
                    const categoryPerms = Object.entries(category.permissions);
                    const grantedPerms = categoryPerms.filter(([key]) => rolePerms.includes(key));

                    if (grantedPerms.length === 0) return null;

                    return (
                      <div key={categoryKey} className="bg-white rounded-lg p-4 border border-slate-200">
                        <h4 className="font-semibold text-slate-800 mb-2">{category.label}</h4>
                        <ul className="space-y-1">
                          {grantedPerms.map(([permKey, permLabel]) => (
                            <li key={permKey} className="flex items-center gap-2 text-sm text-slate-600">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span>{permLabel}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {}
        <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('rolesPermissions.legend')}</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-slate-600">{t('rolesPermissions.permissionGranted')}</span>
            </div>
            <div className="flex items-center gap-2">
              <X className="w-5 h-5 text-slate-300" />
              <span className="text-slate-600">{t('rolesPermissions.permissionDenied')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600 font-medium">3/3</span>
              <span className="text-slate-600">{t('rolesPermissions.allPermissions')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-amber-600 font-medium">2/3</span>
              <span className="text-slate-600">{t('rolesPermissions.partialPermissions')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;